import { Controller } from "@hotwired/stimulus"
import { showAlertModal } from "../lib/custom_confirm"

// Client-side size guard for file-valued AppSetting uploads (the agent-website
// favicon and default logo). The size cap is enforced authoritatively server-side in
// AppSettingAttachment#validate_size, but with no client check the *entire* file
// uploads to Rails before it can be rejected — a long, confusing wait on a large
// file (issue #7638). This rejects an oversized file the instant it's selected,
// before any upload, clears the input so it can never be submitted, and explains
// why via the app's styled alert modal. The server validation remains the
// non-bypassable backstop.
//
// maxBytes is supplied by the view from the setting's per-var rules
// (AppSettingAttachment.rules_for), so the client and server share a single
// source of truth for the limit.
//
// Wired onto the file input in admin/app_settings/_form.html.haml.
export default class extends Controller {
  static values = { maxBytes: Number }

  validate () {
    // No usable limit configured → defer entirely to the server backstop rather
    // than rejecting every file (a 0/absent value would otherwise block all).
    if (!this.hasMaxBytesValue || this.maxBytesValue <= 0) return

    const file = this.element.files?.[0]
    if (!file || file.size <= this.maxBytesValue) return

    // Clear the selection so the oversized file can't reach the server even if
    // the user dismisses the alert and clicks Save anyway.
    this.element.value = ""

    showAlertModal(
      `That file is ${this.formatKb(file.size)} KB, over the ${this.formatKb(this.maxBytesValue)} KB limit. ` +
        "Please choose a smaller image.",
      { title: "File too large", variant: "danger" }
    )
  }

  // Bytes → whole KB with a thousands separator, matching the server-side
  // message ("max 500 KB"). Uses 1024-byte KB to match Rails' `500.kilobytes`.
  formatKb (bytes) {
    return Math.round(bytes / 1024).toLocaleString("en-US")
  }
}
